
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function HomeSecurity() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: 'ri-home-shield-line',
      title: 'Homeowner Security Targeting',
      description: 'Advanced targeting strategies to reach homeowners in specific neighborhoods with security concerns.'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Smart Home Integration',
      description: 'Showcase modern security systems that integrate with smart home technology and mobile apps.'
    },
    {
      icon: 'ri-compare-line',
      title: 'System Comparisons',
      description: 'Interactive tools that help prospects compare security systems, features, and monitoring options.'
    },
    {
      icon: 'ri-calendar-schedule-line',
      title: 'Installation Scheduling',
      description: 'Streamlined booking process for security system consultations and installation appointments.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Neighborhood Analysis',
      description: 'Analyze crime statistics and homeowner demographics to identify high-potential security prospects.'
    },
    {
      step: '02',
      title: 'Trust-Based Campaigns',
      description: 'Create campaigns that build trust and address common security concerns of homeowners.'
    },
    {
      step: '03',
      title: 'Product Education',
      description: 'Educate prospects about modern security technology and monitoring service benefits.'
    },
    {
      step: '04',
      title: 'Consultation Booking',
      description: 'Optimize for in-home consultations and security system demonstrations.'
    }
  ];

  const stats = [
    { number: '380K+', label: 'Home Security Leads' },
    { number: '24%', label: 'Average Conversion Rate' },
    { number: '$225', label: 'Average Cost Per Lead' },
    { number: '82%', label: 'Installation Rate' }
  ];

  const securityFeatures = [
    {
      icon: 'ri-camera-line',
      title: 'Smart Surveillance',
      description: 'HD cameras with mobile monitoring, motion detection, and cloud storage capabilities.',
      features: ['24/7 Recording', 'Mobile Alerts', 'Night Vision', 'Cloud Storage']
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Intrusion Detection',
      description: 'Advanced alarm systems with door/window sensors and motion detectors.',
      features: ['Door/Window Sensors', 'Motion Detectors', 'Glass Break Sensors', 'Panic Buttons']
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: 'Smart Access Control',
      description: 'Keyless entry systems with smart locks and access code management.',
      features: ['Smart Locks', 'Keypad Entry', 'Remote Access', 'Temporary Codes']
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Professional Monitoring',
      description: '24/7 professional monitoring with emergency response coordination.',
      features: ['24/7 Monitoring', 'Emergency Response', 'Police Dispatch', 'Mobile Alerts']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(239, 68, 68, 0.8), rgba(220, 38, 38, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20security%20specialist%20demonstrating%20smart%20home%20security%20system%20to%20interested%20homeowner%20family%20in%20modern%20living%20room%2C%20showcasing%20control%20panel%20and%20mobile%20app%20interface%2C%20clean%20contemporary%20home%20interior%20with%20security%20technology&width=1920&height=800&seq=home-security-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Home Security Lead Generation
            </h1>
            <p className="text-xl md:text-2xl text-red-100 mb-8">
              Generate leads for home security companies targeting homeowners interested in protecting their property and family with advanced security systems, smart home integration, and monitoring services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-black hover:bg-gray-100 font-semibold"
              >
                <i className="ri-shield-line mr-2 text-black"></i>
                Secure More Leads
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate('/case-studies')}
                className="border-white text-white hover:bg-white hover:text-red-600 font-semibold"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                View Results
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Home Security Lead Performance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our security-focused campaigns connect with homeowners who value family protection and home safety.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Modern Home Security Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We showcase the latest in home security technology to attract tech-savvy homeowners seeking comprehensive protection.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                <div className="flex items-start">
                  <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-2xl mr-6 flex-shrink-0">
                    <i className={`${feature.icon} text-2xl text-red-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {feature.features.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center">
                          <div className="w-2 h-2 bg-red-600 rounded-full mr-3"></div>
                          <span className="text-gray-700 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-red-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Targeted Home Security Marketing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our campaigns build trust with homeowners by addressing their specific security concerns and showcasing reliable protection solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Security Lead Generation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A strategic approach that builds trust and addresses homeowner security concerns while generating qualified leads.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-red-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <img 
                src="https://readdy.ai/api/search-image?query=Secure%20family%20home%20at%20night%20with%20professional%20security%20system%20including%20motion%20sensor%20lights%2C%20security%20cameras%2C%20and%20smart%20door%20locks%2C%20modern%20residential%20security%20installation%20with%20monitoring%20service%20indicators&width=600&height=400&seq=security-home&orientation=landscape"
                alt="Secure Home"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Building Trust Through Security
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our home security campaigns focus on building trust with homeowners by addressing their genuine safety concerns and showcasing reliable, professional security solutions.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Neighborhood crime analysis and targeting</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Smart home integration showcasing</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Professional monitoring service emphasis</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Free consultation and installation scheduling</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Protect More Homes, Generate More Leads
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Partner with us to connect with homeowners who prioritize family safety and home protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+910120418794"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call +91 0120 418 7944
            </a>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-red-600"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
